import { LINK_RE } from "./constant";

const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const renderTextWithLinks = (text) => {
    if (!text) return null;

    return text.split(urlRegex).map((part, index) => {
        if (part.match(urlRegex)) {
            const href = part.startsWith("http") ? part : `https://${part}`;
            return (
                <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "blue", textDecoration: "underline" }}
                >
                    {part}
                </a>
            );
        }
        return <span key={index}>{part}</span>;
    });
};

export const renderContent = (text) => {
    if (!text) return null;

    const lines = text.split("\n").filter(Boolean);

    return lines.map((line, index) => {
        const trimmed = line.trim();

        // bullet line support: -, *, •
        if (/^[-*•]\s+/.test(trimmed)) {
            return (
                <ul key={index} style={{ paddingLeft: "20px", margin: "6px 0" }}>
                    <li>{renderTextWithLinks(trimmed.replace(/^[-*•]\s+/, ""))}</li>
                </ul>
            );
        }

        return (
            <p key={index} style={{ margin: "6px 0" }}>
                {renderTextWithLinks(trimmed)}
            </p>
        );
    });
};

export const htmlToPlainText = (content) => {
    if (!content) return "";

    const text = String(content);
    if (typeof DOMParser === "undefined") {
        return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }

    const document = new DOMParser().parseFromString(text, "text/html");
    document.body.querySelectorAll("script, style, noscript, template").forEach((element) => element.remove());
    document.body.querySelectorAll("br").forEach((element) => element.replaceWith("\n"));
    document.body.querySelectorAll("li").forEach((element) => {
        element.prepend("• ");
        element.append("\n");
    });
    document.body.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, blockquote").forEach((element) => {
        element.append("\n");
    });

    return (document.body.textContent || "")
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
};

export const containsHtmlMarkup = (content) => {
    if (!content) return false;
    return /<!doctype\s+html|<([a-z][a-z0-9-]*)(?:\s[^>]*)?>/i.test(String(content));
};

export const buildSafeHtmlDocument = (content) => {
    const source = String(content || "");
    if (typeof DOMParser === "undefined") {
        const escaped = source
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
        return `<!doctype html><html><body>${escaped}</body></html>`;
    }

    const document = new DOMParser().parseFromString(source, "text/html");
    document.querySelectorAll("script, iframe, object, embed, base, form, link[rel='stylesheet'], meta[http-equiv]")
        .forEach((element) => element.remove());

    document.querySelectorAll("*").forEach((element) => {
        [...element.attributes].forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim();
            if (name.startsWith("on") || name === "srcdoc") {
                element.removeAttribute(attribute.name);
            } else if ((name === "href" || name === "src") && /^(javascript|vbscript|data:text\/html):/i.test(value)) {
                element.removeAttribute(attribute.name);
            } else if (name === "style") {
                element.setAttribute("style", value
                    .replace(/expression\s*\([^)]*\)/gi, "")
                    .replace(/url\s*\([^)]*\)/gi, ""));
            }
        });
    });

    document.querySelectorAll("style").forEach((styleElement) => {
        styleElement.textContent = (styleElement.textContent || "")
            .replace(/@import[^;]+;/gi, "")
            .replace(/expression\s*\([^)]*\)/gi, "")
            .replace(/url\s*\([^)]*\)/gi, "");
    });

    document.querySelectorAll("a").forEach((anchor) => {
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
    });

    const safetyStyle = document.createElement("style");
    safetyStyle.textContent = `
        html, body { max-width: 100%; min-height: 0; overflow-x: hidden; }
        img, video, table { max-width: 100%; }
    `;
    document.head.appendChild(safetyStyle);

    return `<!doctype html>${document.documentElement.outerHTML}`;
};

function isEmail(str) {
    return /^([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})$/i.test(str);
}

function isUrl(str) {
    return /^(https?:\/\/|www\.)/i.test(str);
}

function normalizeUrl(str) {
    return str.startsWith("www.") ? `https://${str}` : str;
}

export function renderTextWithLinksAdvanced(text, aColor = "text-primary") {
    if (!text) return null;

    return text.split(LINK_RE).map((part, i) => {
        if (!part) return null;

        if (isEmail(part)) {
            return (
                <a
                    key={i}
                    href={`mailto:${part}`}
                    style={{
                        color: aColor,
                        fontWeight: 500,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                        textShadow: "0 1px 4px rgba(0,0,0,0.12)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                    }}
                >
                    {part}
                </a>
            );
        }

        if (isUrl(part)) {
            const href = normalizeUrl(part);

            return (
                <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                        color: "#7c3aed",
                        fontWeight: 500,
                        textDecoration: "underline",
                        textUnderlineOffset: "4px",
                        textShadow: "0 1px 4px rgba(0,0,0,0.12)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                    }}
                >
                    {part}
                </a>
            );
        }

        return <span key={i}>{part}</span>;
    });
}
